import { Button, Divider, Form, Input, Modal, Radio } from 'antd';
import FormField from 'components/FormField';
import { getStoreCount } from 'components/StoreScopes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AiModelState, getPrefixName, hideRadioClassName, Inline, Prefix } from './share';
// import { updateShipPlace } from 'views/BootGuide/service';
import useSmartField from 'hooks/useSmartField';
import { useTranslate, T } from '@tolgee/react';
import TransComponent from 'components/TransComponent';
import toLowerCase from 'utils/toLowerCase';
import TagSelect from 'components/TagSelect';
import { PlusOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import { Formik } from 'formik';
import { uniq } from 'lodash-es';

type FV = BootGuide.AiModel.FormValues.ShipPlace & {
  customAreas?: string[];
};

export const regionTypeAreasMap: Partial<
  Record<General.UpperCaseRegion, Record<'local' | 'cross' | 'pf', string[]>>
> = {
  BR: {
    local: ['SAO_PAULO', 'RIO_DE_JANEIRO', 'SALVADOR'],
    cross: ['CHINA'],
    pf: ['SAO_PAULO', 'RIO_DE_JANEIRO', 'SALVADOR'],
  },
  TH: {
    local: ['BANGKOK', 'CHIANG_MAI', 'PHUKET'],
    cross: ['CHINA'],
    pf: ['BANGKOK', 'CHIANG_MAI', 'PHUKET'],
  },
  SG: {
    local: ['SINGAPORE_CITY'],
    cross: ['CHINA'],
    pf: ['SINGAPORE_CITY'],
  },
  PH: {
    local: ['MANILA_METROPOLEX', 'BULACAN', 'LAGUNA', 'CEBU', 'DAVAO'],
    cross: ['CHINA'],
    pf: ['MANILA_METROPOLEX', 'BULACAN', 'LAGUNA', 'CEBU', 'DAVAO'],
  },
  VN: {
    local: ['HO_CHI_MINH_CITY', 'HA_NOI_CITY', 'BINH_DUONG_PROVINCE', 'BAC_NINH_PROVINCE'],
    cross: ['CHINA'],
    pf: ['HO_CHI_MINH_CITY', 'HA_NOI_CITY', 'BINH_DUONG_PROVINCE', 'BAC_NINH_PROVINCE'],
  },
  ID: {
    local: ['JAKARTA', 'TANGERANG', 'SURABAYA'],
    cross: ['CHINA'],
    pf: ['JAKARTA', 'TANGERANG', 'SURABAYA'],
  },
  MX: {
    local: [],
    cross: ['CHINA'],
    pf: [],
  },
  MY: {
    local: ['KUALA_LUMPUR', 'IPOH', 'PENANG', 'SEREMBAN', 'SELANGOR'],
    cross: ['CHINA'],
    pf: ['KUALA_LUMPUR', 'IPOH', 'PENANG', 'SEREMBAN', 'SELANGOR'],
  },
  US: {
    local: [],
    cross: ['CHINA'],
    pf: [],
  },
};

export function useAreaLabel() {
  const { t } = useTranslate();

  const areaLabel = useMemo<Partial<Record<string, string>>>(() => {
    return {
      CHINA: t('phoneCountry.cn'),
      BANGKOK: t('Bangkok'),
      CHIANG_MAI: t('清迈'),
      PHUKET: t('普吉岛'),
      MANILA_METROPOLEX: t('马尼拉大都会'),
      BULACAN: t('布拉干'),
      LAGUNA: t('拉古纳'),
      CEBU: t('宿务'),
      DAVAO: t('达沃'),
      JAKARTA: t('jakarta'),
      TANGERANG: t('唐格朗'),
      SURABAYA: t('泗水'),
      KUALA_LUMPUR: t('吉隆坡'),
      IPOH: t('怡保'),
      PENANG: t('槟城'),
      SEREMBAN: t('芙蓉'),
      SELANGOR: t('雪兰莪'),
      SINGAPORE_CITY: t('新加坡市'),
      HO_CHI_MINH_CITY: t('胡志明市'),
      HA_NOI_CITY: t('HanoiCity'),
      BINH_DUONG_PROVINCE: t('平阳省'),
      BAC_NINH_PROVINCE: t('北宁省'),
      SAO_PAULO: t('圣保罗'),
      RIO_DE_JANEIRO: t('里约热内卢'),
      SALVADOR: t('萨尔瓦多'),
    };
  }, [t]);

  const getAreaLabel = useCallback(
    (area: string) => {
      if (areaLabel[area]) {
        return areaLabel[area];
      }
      const [region, storeType, other] = area.split('_');
      if (other) {
        return undefined;
      }
      const regionLabel = t(`region.${toLowerCase(region)}`);
      if (regionLabel && storeType === 'LOCAL') {
        return `${regionLabel}(${t('本土店铺')})`;
      }
      if (regionLabel && storeType === 'CROSS') {
        return `${regionLabel}(${t('跨境店铺')})`;
      }
      return undefined;
    },
    [t, areaLabel],
  );

  return {
    t,
    getAreaLabel,
  };
}

const defaultStoreScopes: General.StoreScope[] = [];

function ShipPlace({
  prefix,
  singleMode,
  storeMap,
}: {
  prefix?: Prefix;
  singleMode?: boolean;
  storeMap: AiModelState<FV>['storeMap'];
}) {
  const { getAreaLabel, t } = useAreaLabel();

  const {
    field: { value: valueType, onChange: _onValueTypeChange },
  } = useSmartField<FV['valueType']>(getPrefixName('valueType', prefix));
  const onValueTypeChange = (valueType: FV['valueType']) => {
    _onValueTypeChange(valueType);
    setTimeout(() => {
      _onValueTypeChange(valueType);
    });
  };
  const {
    field: { value: storeScopes = defaultStoreScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: areasValue = [], onChange: _onAreasChange },
  } = useSmartField<FV['areas']>(getPrefixName('areas', prefix));
  const onAreasChange = (areas: FV['areas']) => {
    _onAreasChange(areas);
    setTimeout(() => {
      _onAreasChange(areas);
    });
  };

  const {
    field: { value: customAreas, onChange: onCustomAreasChange },
  } = useSmartField<FV['customAreas']>(getPrefixName('customAreas', prefix));

  const areas = useMemo(() => {
    return storeScopes.reduce<string[]>((res, { regionStoreScopes }) => {
      return regionStoreScopes.reduce((res, { storeIds }) => {
        return (
          storeIds?.reduce((res, id) => {
            const store = storeMap[id];
            if (!store) {
              return res;
            }
            const { is3pf, storeType, region } = store;
            const {
              [region]: {
                [is3pf ? 'pf' : storeType === 'LOCAL' ? 'local' : 'cross']: areas = [],
              } = {},
            } = regionTypeAreasMap;
            if (areas.length) {
              areas.forEach((area) => {
                if (!res.includes(area)) {
                  res.push(area);
                }
              });
            }
            return res;
          }, res) || res
        );
      }, res);
    }, []);
  }, [storeScopes, storeMap]);

  const refsCurrent = {
    valueType,
    onValueTypeChange,
    areasValue,
    onAreasChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const next = refs.current.areasValue.filter((_) =>
      [...areas, ...(customAreas || [])].includes(_),
    );
    if (next.length < refs.current.areasValue.length) {
      refs.current.onAreasChange(next);
    }
  }, [areas, customAreas]);

  const { visible, show, hide, meta } = useModal<{
    onCreate: (customArea: string) => void;
  }>();

  const inputRef = useRef<Input>(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [visible]);

  const customAreaModal = meta && (
    <Formik
      initialValues={{ value: '' }}
      onSubmit={(values) => {
        onCustomAreasChange(uniq([...(customAreas || []), values.value]));
        meta.onCreate(values.value);
        hide();
      }}
    >
      {({ handleSubmit, resetForm }) => {
        return (
          <Modal
            visible={visible}
            width={448}
            title={t('自定义地区')}
            onCancel={() => hide()}
            onOk={() => handleSubmit()}
            afterClose={() => {
              setTimeout(() => {
                resetForm();
              }, 200);
            }}
          >
            <Form layout="vertical">
              <FormField<string>
                name="value"
                required
                label={t('自定义地区')}
                validate={(value) => {
                  if (!value || !value.trim()) {
                    return t('请输入自定义地区名称\n');
                  }
                  if (customAreas?.includes(value) || areasValue.includes(value)) {
                    return t('地区已存在');
                  }
                }}
              >
                {({ field }) => {
                  return (
                    <Input
                      ref={inputRef}
                      placeholder={t('请输入自定义地区名称\n')}
                      onPressEnter={() => handleSubmit()}
                      {...field}
                    />
                  );
                }}
              </FormField>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );

  const getHideStyle = (type: FV['valueType']) =>
    singleMode && valueType !== type ? { display: 'none' } : undefined;

  return (
    <div>
      {
        <Inline style={getHideStyle('area')} className="mb-[12px]">
          {!singleMode && (
            <Radio
              checked={valueType === 'area'}
              onChange={({ target: { checked } }) => checked && onValueTypeChange('area')}
            ></Radio>
          )}
          <FormField<FV['areas']>
            name={getPrefixName(`areas`, prefix)}
            validate={(value) => {
              if (valueType === 'area' && getStoreCount(storeScopes) > 0) {
                if (!value?.length) {
                  return t('请选择');
                }
              }
            }}
          >
            {({ field: { value, onChange, onBlur } }) => {
              return (
                <Inline onBlur={onBlur}>
                  <T
                    keyName="x发货"
                    params={{
                      x: (
                        <TransComponent
                          content={
                            <TagSelect
                              style={{ minWidth: '190px' }}
                              showArrow
                              mode="multiple"
                              placeholder={t('请选择')}
                              dropdownMatchSelectWidth={false}
                              value={value}
                              onChange={onChange}
                              options={[
                                ...areas.map((area) => {
                                  return {
                                    value: area,
                                    label: getAreaLabel(area),
                                  };
                                }),
                                ...(customAreas || []).map((value) => ({ value, label: value })),
                              ]}
                              dropdownRender={(menu) => (
                                <>
                                  {menu}
                                  <Divider style={{ margin: '8px 0' }} />
                                  <Button
                                    block
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() =>
                                      show({
                                        onCreate: (customArea) => onChange([...value!, customArea]),
                                      })
                                    }
                                  >
                                    {t('自定义地区')}
                                  </Button>
                                </>
                              )}
                            />
                          }
                        />
                      ),
                    }}
                  />
                </Inline>
              );
            }}
          </FormField>
        </Inline>
      }
      <Radio
        className={singleMode ? hideRadioClassName : undefined}
        style={getHideStyle('nearBy')}
        checked={valueType === 'nearBy'}
        onChange={({ target: { checked } }) => checked && onValueTypeChange('nearBy')}
      >
        {t('会就近发货')}
      </Radio>
      {customAreaModal}
    </div>
  );
}

export default ShipPlace;
